import { useState, useEffect, useMemo, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../api/axios';
import { Alert } from '../components/UI';
import { RichEditor, FormatBar } from '../components/RichText';

const MAX_FILE = 15 * 1024 * 1024;
const MAX_FILES = 10;
const ALLOWED = ['pdf', 'doc', 'docx', 'xls', 'xlsx', 'ppt', 'pptx', 'jpg', 'jpeg', 'png'];
const YT = /^https?:\/\/((www|m)\.)?(youtube\.com|youtu\.be|youtube-nocookie\.com)\//i;
const DRAFT_KEY = 'cs_announce_draft_v1';
const fmtSize = (b) => (b >= 1048576 ? `${(b / 1048576).toFixed(1)} MB` : `${Math.max(1, Math.round(b / 1024))} KB`);

const readDraft = () => { try { return JSON.parse(localStorage.getItem(DRAFT_KEY) || 'null'); } catch { return null; } };
const writeDraft = (d) => { try { d ? localStorage.setItem(DRAFT_KEY, JSON.stringify(d)) : localStorage.removeItem(DRAFT_KEY); } catch {} };

const Chip = ({ label, title, tone, onRemove }) => (
  <span title={title} style={{ display:'inline-flex', alignItems:'center', gap:'4px', maxWidth:'100%', padding:'3px 4px 3px 10px', borderRadius:'999px', fontSize:'13px',
    background: tone === 'group' ? 'var(--blue-light)' : 'var(--bg)', color: tone === 'group' ? 'var(--blue)' : 'var(--text)', border:`1px solid ${tone === 'group' ? 'rgba(10,110,180,0.25)' : 'var(--border-md)'}` }}>
    <span style={{ overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{label}</span>
    <button type="button" onClick={onRemove} aria-label={`Remove ${label}`} style={{ border:'none', background:'none', cursor:'pointer', color:'inherit', padding:'0 4px', fontSize:'16px', lineHeight:1 }}>×</button>
  </span>
);

// A tick-list for one group, with a "select all" box (the detailed way to choose people)
function Group({ title, people, picked, onToggle, onToggleAll }) {
  const all  = people.length > 0 && people.every(p => picked.has(p.key));
  const some = people.some(p => picked.has(p.key));
  return (
    <div style={{ border:'1px solid var(--border)', borderRadius:'var(--radius)', marginBottom:'10px', overflow:'hidden', background:'var(--surface)' }}>
      <label style={{ display:'flex', alignItems:'center', gap:'10px', padding:'9px 12px', background:'var(--bg)', cursor:'pointer', fontSize:'13px', fontWeight:'600', color:'var(--text)' }}>
        <input type="checkbox" checked={all} ref={el => { if (el) el.indeterminate = some && !all; }} onChange={()=>onToggleAll(people, !all)} style={{ width:'16px', height:'16px', accentColor:'var(--blue)' }}/>
        All {title} <span style={{ fontWeight:'400', color:'var(--muted)' }}>({people.length})</span>
      </label>
      <div style={{ maxHeight:'200px', overflowY:'auto' }}>
        {people.length === 0 && <div style={{ padding:'10px 12px', fontSize:'12px', color:'var(--hint)' }}>No one with an email address.</div>}
        {people.map(p => (
          <label key={p.key} style={{ display:'flex', alignItems:'center', gap:'10px', padding:'7px 12px', cursor:'pointer', fontSize:'13px', borderTop:'1px solid var(--border)' }}>
            <input type="checkbox" checked={picked.has(p.key)} onChange={()=>onToggle(p.key)} style={{ width:'16px', height:'16px', accentColor:'var(--blue)', flexShrink:0 }}/>
            <span style={{ flex:1, minWidth:0 }}>{p.name}<span style={{ display:'block', fontSize:'11px', color:'var(--muted)', overflow:'hidden', textOverflow:'ellipsis' }}>{p.email}</span></span>
          </label>
        ))}
      </div>
    </div>
  );
}

// Announcements → Compose: laid out like Gmail's "New message" window
export default function AnnouncementForm() {
  const navigate  = useNavigate();
  const editorRef = useRef(null);
  const fileRef   = useRef(null);
  const draft     = useMemo(readDraft, []);

  const [title, setTitle]     = useState(draft?.title || '');
  const [html, setHtml]       = useState(draft?.html || '');
  const [hasText, setHasText] = useState(!!draft?.text);
  const [videos, setVideos]   = useState(draft?.videos || []);
  const [videoOpen, setVideoOpen] = useState(false);
  const [videoUrl, setVideoUrl]   = useState('');
  const [videoErr, setVideoErr]   = useState(null);
  const [files, setFiles]     = useState([]);
  const [fileErr, setFileErr] = useState(null);
  const [lists, setLists]     = useState({ csStaff: [], admins: [] });
  const [picked, setPicked]   = useState(new Set(draft?.picked || []));
  const [query, setQuery]     = useState('');
  const [hi, setHi]           = useState(0);
  const [panel, setPanel]     = useState(false);   // the tick-list panel
  const [loading, setLoading] = useState(true);
  const [busy, setBusy]       = useState(null);
  const [error, setError]     = useState(null);
  const [restored, setRestored] = useState(!!draft);
  const [draftId, setDraftId] = useState(null);    // set once created, so a retry doesn't make a second one
  const [uploaded, setUploaded] = useState(0);

  useEffect(() => {
    api.get('/announcements/recipients')
      .then(({ data }) => setLists({
        csStaff: data.csStaff.map(p => ({ ...p, key:`csStaff:${p.id}`, source:'csStaff' })),
        admins:  data.admins.map(p => ({ ...p, key:`admin:${p.id}`, source:'admin' })),
      }))
      .catch(err => setError(err.response?.data?.message || 'Failed to load the staff lists'))
      .finally(() => setLoading(false));
  }, []);

  const all = useMemo(() => [...lists.csStaff, ...lists.admins], [lists]);
  const byKey = useMemo(() => Object.fromEntries(all.map(p => [p.key, p])), [all]);
  const emailsPicked = useMemo(() => new Set(all.filter(p => picked.has(p.key)).map(p => p.email.toLowerCase())), [all, picked]);

  // Save what's been typed (not the files) so closing the page never loses a message
  useEffect(() => {
    if (busy || draftId) return undefined;
    const t = setTimeout(() => {
      const empty = !title.trim() && !hasText && !videos.length && !picked.size;
      writeDraft(empty ? null : { title, html, text: hasText ? 'x' : '', videos, picked: [...picked] });
    }, 500);
    return () => clearTimeout(t);
  }, [title, html, hasText, videos, picked, busy, draftId]);

  const toggle    = (key) => setPicked(s => { const n = new Set(s); n.has(key) ? n.delete(key) : n.add(key); return n; });
  const toggleAll = (people, on) => setPicked(s => { const n = new Set(s); people.forEach(p => on ? n.add(p.key) : n.delete(p.key)); return n; });

  // To-box chips: a whole group shows as one chip; everyone else as individual chips
  const groupFull = (people) => people.length > 0 && people.every(p => picked.has(p.key));
  const fullGroups = [
    groupFull(lists.csStaff) && { id:'csStaff', label:`All CS Staff (${lists.csStaff.length})`, people: lists.csStaff },
    groupFull(lists.admins)  && { id:'admins',  label:`All Admin Staff (${lists.admins.length})`, people: lists.admins },
  ].filter(Boolean);
  const covered = new Set(fullGroups.flatMap(g => g.people.map(p => p.key)));
  const singles = [...picked].filter(k => !covered.has(k) && byKey[k]).map(k => byKey[k]);

  const matches = query.trim()
    ? all.filter(p => !picked.has(p.key) && `${p.name} ${p.email}`.toLowerCase().includes(query.trim().toLowerCase())).slice(0, 8)
    : [];
  const choose = (p) => { toggle(p.key); setQuery(''); setHi(0); };

  const onToKey = (e) => {
    if (e.key === 'ArrowDown') { e.preventDefault(); setHi(h => Math.min(h + 1, matches.length - 1)); }
    else if (e.key === 'ArrowUp') { e.preventDefault(); setHi(h => Math.max(h - 1, 0)); }
    else if ((e.key === 'Enter' || e.key === ',') && matches[hi]) { e.preventDefault(); choose(matches[hi]); }
    else if (e.key === 'Escape') setQuery('');
    else if (e.key === 'Backspace' && !query && singles.length) toggle(singles[singles.length - 1].key);
  };

  const addVideo = () => {
    const url = videoUrl.trim();
    if (!url) return;
    if (!YT.test(url)) { setVideoErr('Please paste a YouTube link (youtube.com or youtu.be)'); return; }
    if (videos.includes(url)) { setVideoErr('That video is already added'); return; }
    if (videos.length >= 10) { setVideoErr('You can add up to 10 videos'); return; }
    setVideos(v => [...v, url]); setVideoUrl(''); setVideoErr(null); setVideoOpen(false);
  };

  const addFiles = (e) => {
    const chosen = [...(e.target.files || [])]; e.target.value = '';
    const next = [...files];
    for (const f of chosen) {
      const ext = f.name.split('.').pop().toLowerCase();
      if (!ALLOWED.includes(ext)) { setFileErr(`"${f.name}" isn’t allowed. Use PDF, Word, Excel, PowerPoint, JPG or PNG — and share videos as a YouTube link.`); continue; }
      if (f.size > MAX_FILE) { setFileErr(`"${f.name}" is larger than 15 MB`); continue; }
      if (next.length >= MAX_FILES) { setFileErr(`You can attach up to ${MAX_FILES} files`); break; }
      next.push(f); setFileErr(null);
    }
    setFiles(next);
  };

  const discard = () => {
    if ((title || hasText || videos.length || files.length || picked.size) && !window.confirm('Discard this announcement?')) return;
    writeDraft(null); navigate('/announcements');
  };

  const send = async () => {
    setError(null);
    const text = editorRef.current?.getText() || '';
    if (!emailsPicked.size) { setError('Please add at least one person in the To box.'); return; }
    if (!title.trim()) { setError('Please add a subject.'); return; }
    if (!text) { setError('Please write a message.'); return; }
    if (!window.confirm(`Send this announcement to ${emailsPicked.size} ${emailsPicked.size === 1 ? 'person' : 'people'}? It can’t be unsent.`)) return;

    try {
      let id = draftId;
      if (!id) {
        setBusy('Creating…');
        const { data } = await api.post('/announcements', {
          title, messageHtml: editorRef.current.getHtml(), videos,
          recipients: all.filter(p => picked.has(p.key)).map(p => ({ source: p.source, id: p.id })),
        });
        id = data.announcement._id; setDraftId(id);
      }
      for (let i = uploaded; i < files.length; i++) {
        setBusy(`Uploading file ${i + 1} of ${files.length}…`);
        const body = new FormData(); body.append('file', files[i]);
        await api.post(`/announcements/${id}/files`, body, { headers:{ 'Content-Type':'multipart/form-data' } });
        setUploaded(i + 1);
      }
      setBusy('Sending…');
      await api.post(`/announcements/${id}/send`);
      writeDraft(null);
      navigate(`/announcements/${id}`);
    } catch (err) {
      setError((err.response?.data?.message || 'Something went wrong.') + (draftId || uploaded ? ' Your progress is kept — press Send again to continue.' : ''));
    } finally { setBusy(null); }
  };

  const count = emailsPicked.size;
  const row = { display:'flex', alignItems:'flex-start', gap:'10px', padding:'8px 16px', borderBottom:'1px solid var(--border)', position:'relative' };
  const rowLabel = { width:'56px', flexShrink:0, fontSize:'13px', color:'var(--muted)', paddingTop:'6px' };
  const link = { border:'none', background:'none', cursor:'pointer', color:'var(--blue)', fontSize:'12px', padding:'4px 6px', fontFamily:'inherit' };
  const barBtn = { width:'32px', height:'32px', display:'inline-flex', alignItems:'center', justifyContent:'center', border:'none', borderRadius:'6px', cursor:'pointer', background:'transparent', color:'var(--muted)' };

  if (loading) return <div style={{ minHeight:'100vh', display:'flex', alignItems:'center', justifyContent:'center' }}><i className="ti ti-loader" style={{ fontSize:'24px', color:'var(--blue)' }} aria-hidden="true"/></div>;

  return (
    <div style={{ minHeight:'100vh', background:'var(--bg)', padding:'16px 12px 40px' }}>
      <div style={{ maxWidth:'860px', margin:'0 auto' }}>
        {error && <Alert type="error">{error}</Alert>}
        {restored && (
          <Alert type="info">
            Your unsent draft was restored.{' '}
            <button type="button" onClick={() => { writeDraft(null); window.location.reload(); }} style={{ ...link, padding:0, textDecoration:'underline' }}>Start fresh</button>
          </Alert>
        )}

        <div style={{ background:'var(--surface)', border:'1px solid var(--border-md)', borderRadius:'12px', boxShadow:'0 8px 28px rgba(0,0,0,0.12)', overflow:'hidden' }}>
          {/* Header bar */}
          <div style={{ display:'flex', alignItems:'center', background:'#2f3b4a', color:'#fff', padding:'10px 14px' }}>
            <span style={{ flex:1, fontSize:'14px', fontWeight:'500' }}>New Announcement</span>
            <button type="button" onClick={discard} aria-label="Close" style={{ border:'none', background:'none', color:'#fff', cursor:'pointer', fontSize:'20px', lineHeight:1 }}>×</button>
          </div>

          {/* To */}
          <div style={row}>
            <div style={rowLabel}>To</div>
            <div style={{ flex:1, minWidth:0, display:'flex', flexWrap:'wrap', gap:'5px', alignItems:'center' }}>
              {fullGroups.map(g => <Chip key={g.id} tone="group" label={g.label} onRemove={() => toggleAll(g.people, false)}/>)}
              {singles.map(p => <Chip key={p.key} label={p.name} title={p.email} onRemove={() => toggle(p.key)}/>)}
              <input value={query} onChange={e => { setQuery(e.target.value); setHi(0); }} onKeyDown={onToKey} aria-label="Add people"
                placeholder={picked.size ? '' : 'Type a name or choose a group'}
                style={{ flex:'1 1 140px', minWidth:'120px', border:'none', outline:'none', padding:'6px 2px', fontSize:'14px', background:'transparent', color:'var(--text)' }}/>
            </div>
            <div style={{ flexShrink:0, display:'flex', flexWrap:'wrap', justifyContent:'flex-end' }}>
              <button type="button" style={link} onClick={() => toggleAll(lists.csStaff, true)}>+ CS Staff</button>
              <button type="button" style={link} onClick={() => toggleAll(lists.admins, true)}>+ Admin Staff</button>
              <button type="button" style={link} onClick={() => setPanel(p => !p)}>{panel ? 'Hide list' : 'Choose…'}</button>
            </div>

            {matches.length > 0 && (
              <div role="listbox" style={{ position:'absolute', left:'82px', right:'16px', top:'100%', zIndex:20, background:'var(--surface)', border:'1px solid var(--border-md)', borderRadius:'var(--radius)', boxShadow:'0 6px 20px rgba(0,0,0,0.15)', maxHeight:'280px', overflowY:'auto' }}>
                {matches.map((p, i) => (
                  <div key={p.key} role="option" aria-selected={i === hi} onMouseDown={e => { e.preventDefault(); choose(p); }} onMouseEnter={() => setHi(i)}
                    style={{ padding:'8px 12px', cursor:'pointer', fontSize:'13px', background: i === hi ? 'var(--blue-light)' : 'transparent' }}>
                    {p.name} <span style={{ color:'var(--muted)' }}>· {p.email}</span>
                    <span style={{ float:'right', fontSize:'11px', color:'var(--hint)' }}>{p.source === 'csStaff' ? 'CS Staff' : 'Admin'}</span>
                  </div>
                ))}
              </div>
            )}
          </div>

          {panel && (
            <div style={{ padding:'12px 16px', background:'var(--bg)', borderBottom:'1px solid var(--border)' }}>
              <Group title="CS Staff" people={lists.csStaff} picked={picked} onToggle={toggle} onToggleAll={toggleAll}/>
              <Group title="Admin Staff" people={lists.admins} picked={picked} onToggle={toggle} onToggleAll={toggleAll}/>
              <div style={{ fontSize:'12px', color:'var(--muted)' }}>Anyone in both lists only gets one email.</div>
            </div>
          )}

          {/* Subject */}
          <div style={row}>
            <div style={rowLabel}>Subject</div>
            <input value={title} onChange={e => setTitle(e.target.value)} maxLength={150} aria-label="Subject"
              style={{ flex:1, border:'none', outline:'none', padding:'6px 2px', fontSize:'14px', background:'transparent', color:'var(--text)' }}/>
          </div>

          {/* Body */}
          <RichEditor ref={editorRef} initialHtml={draft?.html} placeholder="Write your message…" minHeight={260}
            onChange={(h, text) => { setHtml(h); setHasText(!!text); }}/>

          {/* Attachments and videos, shown under the message like Gmail */}
          {(files.length > 0 || videos.length > 0 || fileErr || videoOpen) && (
            <div style={{ padding:'10px 16px', borderTop:'1px solid var(--border)', display:'flex', flexWrap:'wrap', gap:'8px', alignItems:'center' }}>
              {files.map((f, i) => (
                <span key={`${f.name}-${i}`} style={{ display:'inline-flex', alignItems:'center', gap:'6px', padding:'5px 6px 5px 10px', border:'1px solid var(--border-md)', borderRadius:'8px', fontSize:'12px', background:'var(--bg)', maxWidth:'100%' }}>
                  <i className="ti ti-file" style={{ color:'var(--blue)' }} aria-hidden="true"/>
                  <span style={{ overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{f.name} <span style={{ color:'var(--muted)' }}>({fmtSize(f.size)})</span></span>
                  {i < uploaded ? <span style={{ color:'var(--green)' }}>✓</span>
                    : <button type="button" onClick={() => setFiles(l => l.filter((_, n) => n !== i))} aria-label={`Remove ${f.name}`} style={{ border:'none', background:'none', cursor:'pointer', color:'var(--muted)', fontSize:'16px', lineHeight:1 }}>×</button>}
                </span>
              ))}
              {videos.map((v, i) => (
                <span key={v} style={{ display:'inline-flex', alignItems:'center', gap:'6px', padding:'5px 6px 5px 10px', border:'1px solid var(--border-md)', borderRadius:'8px', fontSize:'12px', background:'var(--bg)', maxWidth:'100%' }}>
                  <i className="ti ti-brand-youtube" style={{ color:'var(--red)' }} aria-hidden="true"/>
                  <span style={{ overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap', maxWidth:'260px' }}>{v}</span>
                  <button type="button" onClick={() => setVideos(l => l.filter((_, n) => n !== i))} aria-label="Remove video" style={{ border:'none', background:'none', cursor:'pointer', color:'var(--muted)', fontSize:'16px', lineHeight:1 }}>×</button>
                </span>
              ))}
              {videoOpen && (
                <span style={{ display:'inline-flex', gap:'6px', flex:'1 1 280px' }}>
                  <input autoFocus value={videoUrl} onChange={e => { setVideoUrl(e.target.value); setVideoErr(null); }} onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); addVideo(); } if (e.key === 'Escape') setVideoOpen(false); }}
                    placeholder="Paste a YouTube link" aria-label="YouTube link" style={{ flex:1, minWidth:0, padding:'6px 10px', fontSize:'13px', border:'1px solid var(--border-md)', borderRadius:'var(--radius)', background:'var(--surface)', color:'var(--text)' }}/>
                  <button type="button" onClick={addVideo} style={{ ...link, border:'1px solid var(--border-md)', borderRadius:'var(--radius)' }}>Add</button>
                </span>
              )}
              {(fileErr || videoErr) && <div role="alert" style={{ flexBasis:'100%', color:'var(--red)', fontSize:'12px' }}>{fileErr || videoErr}</div>}
            </div>
          )}

          {/* Bottom bar: Send, formatting, attach, video, discard */}
          <div style={{ display:'flex', alignItems:'center', gap:'6px', flexWrap:'wrap', padding:'10px 12px', borderTop:'1px solid var(--border)' }}>
            <button type="button" onClick={send} disabled={!!busy}
              style={{ padding:'9px 22px', background: busy ? 'var(--hint)' : 'var(--blue)', color:'#fff', border:'none', borderRadius:'999px', fontSize:'14px', fontWeight:'600', cursor: busy ? 'not-allowed' : 'pointer', fontFamily:'inherit' }}>
              {busy || 'Send'}
            </button>
            <FormatBar editor={editorRef}/>
            <button type="button" style={barBtn} title="Attach files" aria-label="Attach files" onClick={() => fileRef.current?.click()}><i className="ti ti-paperclip" style={{ fontSize:'18px' }} aria-hidden="true"/></button>
            <input ref={fileRef} type="file" multiple accept=".pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.jpg,.jpeg,.png" onChange={addFiles} style={{ display:'none' }}/>
            <button type="button" style={barBtn} title="Add a YouTube video" aria-label="Add a YouTube video" onClick={() => setVideoOpen(o => !o)}><i className="ti ti-brand-youtube" style={{ fontSize:'18px' }} aria-hidden="true"/></button>
            <span style={{ marginLeft:'auto', fontSize:'12px', color:'var(--muted)' }}>{count ? `To ${count} ${count === 1 ? 'person' : 'people'}` : ''}</span>
            <button type="button" style={barBtn} title="Discard" aria-label="Discard" onClick={discard}><i className="ti ti-trash" style={{ fontSize:'18px' }} aria-hidden="true"/></button>
          </div>
        </div>

        <p style={{ fontSize:'12px', color:'var(--hint)', margin:'12px 4px 0', lineHeight:1.5 }}>
          Each person gets their own email and their own private link to the files and videos, so they never see each other’s addresses. Share videos as a YouTube link (Unlisted works well).
        </p>
      </div>
    </div>
  );
}
